import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { GenericStreamsService } from '../../../service/generic-streams.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import moment, { Moment } from 'moment';
import { provideMomentDateAdapter } from '@angular/material-moment-adapter';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs';
import { AccountsService } from '../../../service/accounts.service';

const RANGE_START_DATE = moment().startOf('month').subtract(3, 'months');
const RANGE_END_DATE = moment().endOf('month');;

// See the Moment.js docs for the meaning of these formats:
// https://momentjs.com/docs/#/displaying/format/
const MM_YYYY_FORMAT = {
    parse: {
        dateInput: 'MM/YYYY',
    },
    display: {
        dateInput: 'MM/YYYY',
        monthYearLabel: 'MMM YYYY',
        dateA11yLabel: 'LL',
        monthYearA11yLabel: 'MMMM YYYY',
    },
};

@Component({
  selector: 'app-conversation-view',
  standalone: false,
  templateUrl: './conversation-view.component.html',
  styleUrl: './conversation-view.component.scss',
  providers: [provideMomentDateAdapter(MM_YYYY_FORMAT)]
})
export class ConversationViewComponent implements OnInit {

  route = inject(ActivatedRoute);
  streamService = inject(GenericStreamsService);
  accountService = inject(AccountsService);
  streamId!: string;

  streamInfo: any;

  activeStep: any = null;
  activeStepNumber: number | undefined;

  sessions: any[] = [];
  activeSession: any = null;

  // filter
  searchQuery = new FormControl('');
  protected range = new FormGroup({
        start: new FormControl<Moment>(
            RANGE_START_DATE,
            {
                nonNullable: true,
                validators: [Validators.required],
                updateOn: 'blur',
            },
        ),
        end: new FormControl<Moment>(
            RANGE_END_DATE,
            {
                nonNullable: true,
                validators: [
                    Validators.required,
                ],
                updateOn: 'blur',
            },
        ),
    });
    protected minDate = moment([2025, 0, 1]);
    protected maxDate = RANGE_END_DATE;
    selectionIndex = 0;
    searchUsers: any[] = [];
    selectedUser: any = null;

  

  async ngOnInit() {
    const streamId = this.route.snapshot.paramMap.get('streamId');
    if(!streamId) return;

    const sessions = await this.streamService.getConversationsForStream(streamId, 'all');
    if(sessions === false) return;

    sessions.sort((a: any, b: any) => b.lastActivity - a.lastActivity)

    const stream = await this.streamService.getOneGenericStream(streamId);

    this.streamId = streamId;
    this.streamInfo = stream;
    this.sessions = sessions;

    console.log('STREAM INFO', this.streamInfo)
    console.log('SESSIONS', this.sessions)

    this.searchQuery.valueChanges.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      switchMap(value => {
        if(!value) {
          return [];
        } 
        return this.accountService.searchUsers(value);
      })
    ).subscribe((result : any) => {
      if(result !== false) {
        this.searchUsers = result.users;
        console.log(this.searchUsers)
      }
    })
  }

  async setStepForView(step: any, i: number) {
    this.activeStep = step;
    this.activeStepNumber = i + 1;
  }

  getLatestEntryDate(entries: any) {
    if(!entries || entries.length === 0) return null;

    return Math.max(...entries.map((e: any) => e.dateCreate))
  }

  getSessionLatestDate(session: { steps: { entries: { dateCreate: number }[] }[] }): number | null {
    if (!session?.steps || session.steps.length === 0) return null;

    let latest = 0;

    for (const step of session.steps) {
      if (!step.entries || step.entries.length === 0) continue;

      const stepLatest = Math.max(...step.entries.map(e => e.dateCreate));
      if (stepLatest > latest) latest = stepLatest;
    }

    return latest || null;
  }

  getAllSessionEntries(session: { steps: { entries: any[] }[] }) {
    return session?.steps?.flatMap(step => step.entries || []).length;
  }

  getEntriesNumberForStep(step: any) {
    return step?.entries.length || 0;
  }

  selectSession(session: any) {
  this.activeSession = {
    ...session,
    steps: session.steps
      .map((s: any) => {
        const stepInfo = this.streamInfo.steps.find(
          (st: any) => st.id === s.stepId
        );

        return {
          ...stepInfo,
          ...s,
          entries: [...(s.entries || [])].sort(
            (a, b) => a.dateCreate - b.dateCreate
          )
        };
      })
  };


  this.activeStepNumber = 1;
  this.activeStep = this.activeSession.steps?.[0];

  console.log("ACTIVE", this.activeSession);
}

  back() {
    this.activeSession = null;
    this.activeStep = null;
  }

  async changeTimeFilter(selection: any) {
    this.selectionIndex = selection.index;
    switch(this.selectionIndex) {
      case 0: {
        this.sessions = await this.streamService.getConversationsForStream(this.streamId, 'all', this.selectedUser?.id || null);
        break;
      }
      case 1: {
        this.sessions = await this.streamService.getConversationsForStream(this.streamId, 'today', this.selectedUser?.id || null);
        break;
      }
      case 2: {
        this.sessions = await this.streamService.getConversationsForStream(this.streamId, 'last7', this.selectedUser?.id || null);
        break;
      }
      case 3: {
        this.sessions = [];
        break;
      }
    }
  }

  async onCustomSelect() {
    if(this.range.value.start?.toDate().getTime() && this.range.value.end?.toDate().getTime()) {
          this.sessions = await this.streamService.getConversationsForStream(this.streamId, 'custom', this.selectedUser?.id || null, this.range.value.start?.toDate().getTime(), this.range.value.end?.toDate().getTime());
    }
  }

  async selectUser(user: any) {
    this.selectedUser = user;
    this.searchQuery.setValue("");
    this.searchUsers = [];
    this.changeTimeFilter({index: this.selectionIndex})
  }

  async removeSelection() {
    this.selectedUser = null;
    this.changeTimeFilter({index: this.selectionIndex})
  }
}
